import { useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import AnimatedBackground from './components/AnimatedBackground'
import ScrollProgress from './components/ScrollProgress'
import SiteFooter from './components/SiteFooter'
import SiteHeader from './components/SiteHeader'
import {
  type NavItem,
  type RouteConfig,
  routes,
  solutionsChildren,
} from './siteData'

function normalizePath(pathname: string) {
  if (pathname === '/') {
    return '/'
  }

  return pathname.replace(/\/+$/, '')
}

function App() {
  const [pathname, setPathname] = useState(() => normalizePath(window.location.pathname))

  useEffect(() => {
    const handlePopState = () => {
      setPathname(normalizePath(window.location.pathname))
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const navigate = (nextPath: string) => {
    const normalized = normalizePath(nextPath)
    if (normalized === pathname) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    window.history.pushState({}, '', normalized)
    setPathname(normalized)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const currentRoute = useMemo<RouteConfig>(() => {
    return routes.find((route) => route.path === pathname) ?? routes[0]
  }, [pathname])

  useEffect(() => {
    const pageMeta: Record<string, { title: string; description: string }> = {
      '/': {
        title: 'Yethirihwanonhase | Indigenous-Owned Advisory & Implementation Firm',
        description:
          'Yethirihwanonhase is an Indigenous-owned firm specializing in OCAP governance, digital infrastructure, AI readiness, and vendor risk assessment. Our flagship platform is Rez-Hub.',
      },
      '/platform': {
        title: 'Rez-Hub Platform | Yethirihwanonhase',
        description:
          'Rez-Hub is our flagship sovereign digital platform combining infrastructure, governance, and AI readiness. Delivered by Yethirihwanonhase.',
      },
      '/services': {
        title: 'Services | Yethirihwanonhase',
        description:
          'Advisory and implementation services: OCAP governance, infrastructure design, AI readiness, vendor risk assessment, and Rez-Hub platform deployment.',
      },
      '/ocap': {
        title: 'OCAP Expertise | Yethirihwanonhase',
        description:
          'OCAP-aligned governance consulting and implementation support. Turn stewardship principles into operational practice.',
      },
      '/ai-readiness': {
        title: 'AI Readiness | Yethirihwanonhase',
        description:
          'Governance-first AI readiness assessments, policy development, and responsible deployment planning.',
      },
      '/security-trust': {
        title: 'Security & Trust | Yethirihwanonhase',
        description:
          'Our approach to governance alignment, access controls, privacy, and vendor risk in high-trust digital environments.',
      },
      '/about': {
        title: 'About | Yethirihwanonhase',
        description:
          'Learn about Yethirihwanonhase, the Indigenous-owned company behind Rez-Hub, and its focus on sovereignty, governance, and responsible modernization.',
      },
      '/bios': {
        title: 'Leadership | Yethirihwanonhase',
        description:
          'Meet the leadership and partner profiles supporting Rez-Hub and the broader Yethirihwanonhase delivery model.',
      },
      '/case-studies': {
        title: 'Case Studies | Yethirihwanonhase',
        description:
          'Anonymized scenarios showing how we address governance, infrastructure, and readiness challenges in high-trust environments.',
      },
      '/resources': {
        title: 'Resources | Yethirihwanonhase',
        description:
          'Governance, security, and AI readiness resources to support internal stakeholder alignment and decision-making.',
      },
      '/contact': {
        title: 'Contact | Yethirihwanonhase',
        description:
          'Book a strategic consultation about OCAP governance, infrastructure advisory, AI readiness, or vendor risk assessment.',
      },
    }

    const activeMeta = pageMeta[pathname] ?? pageMeta['/']
    document.title = activeMeta.title

    const descriptionTag = document.querySelector('meta[name="description"]')
    if (descriptionTag) {
      descriptionTag.setAttribute('content', activeMeta.description)
    }
  }, [pathname])

  const navItems: NavItem[] = useMemo(
    () => [
      { label: 'Services', path: '/services', children: solutionsChildren },
      {
        label: 'Expertise',
        path: '/ocap',
        children: [
          {
            label: 'OCAP & Governance',
            path: '/ocap',
            description: 'Turn stewardship principles into operational practice.',
          },
          {
            label: 'Infrastructure Advisory',
            path: '/solutions/infrastructure',
            description: 'Sovereign infrastructure design and implementation.',
          },
          {
            label: 'AI Readiness',
            path: '/ai-readiness',
            description: 'Governance-first AI preparation and policy development.',
          },
          {
            label: 'Risk Assessment',
            path: '/solutions/risk-advisory',
            description: 'Vendor risk, dependency mapping, and governance gaps.',
          },
        ],
      },
      { label: 'Rez-Hub Platform', path: '/platform' },
      { label: 'About', path: '/about' },
      {
        label: 'Resources',
        path: '/resources',
        children: [
          {
            label: 'Insights & Resources',
            path: '/resources',
            description: 'Governance, security, and AI readiness resources.',
          },
          {
            label: 'Case Studies',
            path: '/case-studies',
            description: 'Real-world scenarios and outcomes.',
          },
          {
            label: 'Security & Trust',
            path: '/security-trust',
            description: 'Our approach to governance and risk.',
          },
        ],
      },
      { label: 'Contact', path: '/contact' },
    ],
    [],
  )

  const CurrentPage = currentRoute.component

  return (
    <div className="relative min-h-screen bg-charcoal text-birch">
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>
      <ScrollProgress />
      <AnimatedBackground />
      <SiteHeader currentPath={pathname} navItems={navItems} onNavigate={navigate} />
      <motion.main
        key={currentRoute.path}
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
      >
        <CurrentPage onNavigate={navigate} />
      </motion.main>
      <SiteFooter onNavigate={navigate} />
    </div>
  )
}

export default App
